import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";

import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import {
  Tabs,
  Tab,
  Box,
  Typography,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";

import { Dashboard } from "@mui/icons-material";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { StarRate } from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import SeatMap from "../components/SeatMap";
import { AuthContext } from "../context/AuthContext";
import LibraryBookingHistory from "./LibraryBookingHistory";
import LibraryBookingForm from "./LibraryBookingForm";
import StudentComplaints from "./StudentComplaints";
import StudentReview from "./StudentReview";
import StudentProfile from "./StudentProfile";
import StudentBill from "./StudentBill";

const API = process.env.REACT_APP_API_URL;

const menuItems = [
  { text: "Dashboard", icon: <Dashboard /> },
  { text: "Library History", icon: <LocalLibraryOutlinedIcon /> },
  { text: "Renew Booking", icon: <AutorenewIcon /> },
  { text: "Complaints", icon: <ReportProblemIcon /> },
  { text: "Bill", icon: <ReceiptOutlinedIcon /> },
  { text: "Review", icon: <StarRate /> },
  { text: "Logout", icon: <LogoutOutlinedIcon /> },
];

const StudentDashboard = () => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [librarySeats, setLibrarySeats] = useState([]);
  const [profile, setProfile] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Dashboard");
  const [activeTab, setActiveTab] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedSeatId, setSelectedSeatId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // 🔹 Renewal info (popup + Renew tab)
  const [renewalInfo, setRenewalInfo] = useState(null);
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false); // Renew tab loader
  const [renewSubmitting, setRenewSubmitting] = useState(false);
  const [renewMonths, setRenewMonths] = useState(3);

  const sideNavigationHandler = (route) => {
    if (route === "Dashboard") {
      setSelectedTab("Dashboard");
      fetchAllData();
      fetchRenewalAlert(false);
    } else if (route === "Logout") {
      handleLogout();
      return;
    } else {
      setSelectedTab(route);
      if (route === "Renew Booking") {
        fetchRenewalAlert(true); // Renew tab pe aate hi fresh data
      }
    }
    setMobileOpen(false);
  };

  const fetchAllData = async () => {
    const loadingToast = toast.loading("Loading...");
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [libraryRes, profileRes] = await Promise.all([
        axios.get(`${API}library/seats/live-map/`, config),
        axios.get(`${API}users/me/`, config),
      ]);

      setLibrarySeats(libraryRes.data);
      setProfile(profileRes.data);
      localStorage.setItem("student_id", profileRes.data.id);
      toast.dismiss(loadingToast);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Session expired. Please login again");
      logout();
      navigate("/");
    }
  };

  // 🔹 Renewal alert (for popup + Renew tab)
  const fetchRenewalAlert = async (withLoader = false) => {
    try {
      if (withLoader) setRenewLoading(true);

      const token = localStorage.getItem("token");
      if (!token) return;

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}library/renewal-alert/`, config);
      const data = res.data;
      setRenewalInfo(data || null);

      // Popup sirf Dashboard pe jab can_renew true ho
      if (
        selectedTab === "Dashboard" &&
        data &&
        data.can_renew &&
        data.booking
      ) {
        setRenewModalOpen(true);
      } else if (selectedTab !== "Dashboard") {
        setRenewModalOpen(false);
      }
    } catch (err) {
      console.error("Renewal alert fetch error:", err);
    } finally {
      if (withLoader) setRenewLoading(false);
    }
  };

  const refreshAvailabilityMaps = async () => {
    setIsRefreshing(true);
    const loadingToast = toast.loading("🔄 Refreshing availability maps...");
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [libraryRes] = await Promise.all([
        axios.get(`${API}library/seats/live-map/`, config),
      ]);

      setLibrarySeats(libraryRes.data);

      toast.dismiss(loadingToast);
      toast.success("Availability maps refreshed successfully!", {
        duration: 2000,
        icon: "🔄",
      });
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to refresh availability maps");
      console.error("Refresh error:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    fetchRenewalAlert(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAllData();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
    if (selectedTab === "Dashboard") {
      fetchRenewalAlert(false);
    } else if (selectedTab === "Renew Booking") {
      fetchRenewalAlert(true);
    } else {
      setRenewModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleLibrarySeatClick = (seat) => {
    setSelectedSeatId(seat?.id);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  // 🔹 Confirm renewal (deposit hamesha 0)
  const handleRenewSubmit = async () => {
    if (!renewalInfo || !renewalInfo.booking) return;

    try {
      setRenewSubmitting(true);
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Session expired. Please login again");
        logout();
        navigate("/");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const loadingToast = toast.loading("Renewing your library booking...");

      await axios.post(
        `${API}library/bookings/${renewalInfo.booking.id}/renew/`,
        { months: renewMonths },
        config
      );

      toast.dismiss(loadingToast);
      toast.success("Library booking renewed successfully!");

      await fetchRenewalAlert(true);
      await fetchAllData();
    } catch (err) {
      console.error("Renewal error:", err);
      toast.dismiss();
      const msg =
        err?.response?.data?.detail ||
        "Failed to renew booking. Please try again.";
      toast.error(msg);
    } finally {
      setRenewSubmitting(false);
    }
  };

  // 🔹 Bill calculation for Renew tab
  const monthlyFee =
    (renewalInfo && renewalInfo.monthly_fee) ||
    600; // backend se aaye to use karo, warna default ₹600
  const depositAmount = 0; // renew pe hamesha 0
  const totalAmount = monthlyFee * renewMonths + depositAmount;

  const drawerContent = (
    <Box className="h-full bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
      <Box className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500" />
      <Box className="absolute top-20 right-0 w-32 h-32 bg-blue-500/10 rounded-full -translate-y-16 translate-x-16" />
      <Box className="absolute bottom-20 left-0 w-24 h-24 bg-purple-500/10 rounded-full translate-y-12 -translate-x-12" />

      <Box className="relative z-10 h-full flex flex-col">
        <Box className="p-6 border-b border-gray-700/50">
          <Box className="flex items-center gap-4">
            <Avatar
              src="/images/logo3.png"
              alt="Prof.S.S. Patil Study Center"
              className="w-14 h-14 border-2 border-blue-500/30 shadow-lg"
            />
            <Box>
              <Typography variant="h6" className="font-bold text-white mb-1">
                Prof. S. S. Patil Study Center
              </Typography>
              <Typography
                variant="caption"
                className="text-gray-400 bg-gray-800/50 px-2 py-1 rounded-full"
              >
                Student Portal
              </Typography>
            </Box>
          </Box>
        </Box>

        <List className="py-4 flex-1">
          {menuItems.map((item) => (
            <ListItem
              button
              key={item.text}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                sideNavigationHandler(item.text);
                setTimeout(() => setMobileOpen(false), 50);
                setTimeout(() => setMobileOpen(false), 200);
              }}
              className={`mb-1 mx-3 rounded-xl cursor-pointer transition-all duration-300 ${
                selectedTab === item.text
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25"
                  : "text-gray-300 hover:bg-gray-700/50 hover:text-white"
              }`}
            >
              <ListItemIcon className="min-w-0 mr-3">
                <Box
                  className={`p-1 rounded-lg ${
                    selectedTab === item.text
                      ? "bg-white/20 text-white"
                      : "bg-transparent"
                  }`}
                >
                  {React.cloneElement(item.icon, {
                    className:
                      selectedTab === item.text
                        ? "text-white"
                        : item.text === "Logout"
                        ? "text-red-600"
                        : "text-blue-600",
                  })}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                className={selectedTab === item.text ? "font-semibold" : ""}
              />
              {selectedTab === item.text && (
                <Box className="w-2 h-2 bg-white rounded-full animate-pulse" />
              )}
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box className="flex bg-gray-50 min-h-screen">
      {/* Mobile Menu Button */}
      <Box className="md:hidden fixed top-4 right-4 z-[30]">
        <IconButton
          onClick={() => setMobileOpen(true)}
          className="bg-white shadow-lg hover:bg-gray-50"
          size="large"
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{
          keepMounted: false,
          disableEscapeKeyDown: false,
          disableAutoFocus: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
          onBackdropClick: () => setMobileOpen(false),
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
          zIndex: 55,
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
          zIndex: 10,
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box className="flex-1 md:ml-[280px]">
        {/* Header */}
        <Box className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-20">
          <Box className="flex items-center justify-between p-4 md:p-6">
            <Box className="flex items-center gap-3">
              <Box
                className={`p-3 rounded-xl shadow-lg ${
                  selectedTab === "Dashboard" &&
                  "bg-gradient-to-r from-blue-500 to-indigo-600"
                } ${
                  selectedTab === "Library History" &&
                  "bg-gradient-to-r from-green-600 to-green-700"
                } ${
                  selectedTab === "Renew Booking" &&
                  "bg-gradient-to-r from-blue-600 to-indigo-700"
                } ${
                  selectedTab === "Complaints" &&
                  "bg-gradient-to-r from-red-600 to-red-700"
                } ${
                  selectedTab === "Bill" &&
                  "bg-gradient-to-r from-yellow-600 to-orange-600"
                } ${
                  selectedTab === "Review" &&
                  "bg-gradient-to-r from-yellow-500 to-orange-600"
                }`}
              >
                {selectedTab === "Dashboard" && (
                  <Dashboard className="text-white text-2xl" />
                )}
                {selectedTab === "Library History" && (
                  <LocalLibraryOutlinedIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Renew Booking" && (
                  <AutorenewIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Complaints" && (
                  <ReportProblemIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Bill" && (
                  <ReceiptOutlinedIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Review" && (
                  <StarRate className="text-white text-2xl" />
                )}
              </Box>
              <Box>
                <span className="mb-1 text-lg sm:text-3xl font-bold text-gray-800">
                  {selectedTab}
                </span>
                <Typography sx={{ fontSize: "16px" }} className="text-gray-600">
                  {selectedTab === "Dashboard" && "View library seats"}
                  {selectedTab === "Library History" &&
                    "Library booking records"}
                  {selectedTab === "Renew Booking" &&
                    "Check expiry and renew your booking"}
                  {selectedTab === "Complaints" && "Track complaints"}
                  {selectedTab === "Bill" && "Generate monthly bills"}
                  {selectedTab === "Review" && "Reviews and feedback"}
                </Typography>
              </Box>
            </Box>
            <Box className="flex items-center gap-2 md:gap-3">
              <Box className="hidden md:flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span>Last updated:</span>
                <span className="font-medium">
                  {new Date().toLocaleTimeString()}
                </span>
              </Box>
              {profile && (
                <Box className="relative right-16 md:right-0">
                  <StudentProfile
                    open={dropdownOpen}
                    onClose={() => setDropdownOpen(false)}
                    onProfileUpdate={() => {
                      fetchAllData();
                    }}
                  />
                  <Avatar
                    src={
                      profile.profile_photo ||
                      `https://ui-avatars.com/api/?name=${profile.first_name}+${profile.last_name}`
                    }
                    alt="profile"
                    className="w-10 h-10 border-2 border-white shadow-md hover:scale-105 transition-transform cursor-pointer"
                    onClick={toggleDropdown}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Content Area */}
        <Box className="min-h-screen">
          {/* Dashboard */}
          {selectedTab === "Dashboard" && (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 md:p-6">
              <div className="max-w-full mx-auto space-y-4 md:space-y-6">
                <div className="bg-white rounded-xl shadow-lg p-3 md:p-6 dashboard-card">
                  <div className="flex items-center justify-between mb-3 md:mb-6">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="p-2 md:p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl">
                        <BusinessRoundedIcon className="text-white text-lg md:text-2xl" />
                      </div>
                      <div>
                        <h2 className="text-lg md:2xl font-bold text-gray-800">
                          Availability Maps
                        </h2>
                        <p className="text-gray-600 text-xs md:text-sm">
                          Library seat availability
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outlined"
                      onClick={refreshAvailabilityMaps}
                      disabled={isRefreshing}
                      sx={{
                        borderRadius: "12px",
                        borderColor: "#8b5cf6",
                        color: "#8b5cf6",
                        "&:hover": {
                          borderColor: "#7c3aed",
                          backgroundColor: "rgba(139, 92, 246, 0.04)",
                        },
                        "&:disabled": {
                          borderColor: "#d1d5db",
                          color: "#9ca3af",
                        },
                        minWidth: "40px",
                        width: "40px",
                        height: "40px",
                      }}
                    >
                      <RefreshIcon
                        className={`${isRefreshing ? "animate-spin" : ""}`}
                        fontSize="small"
                      />
                    </Button>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-2 md:p-4 mb-3 md:mb-4">
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      indicatorColor="primary"
                      textColor="primary"
                      className="mb-3 md:mb-4"
                      sx={{
                        "& .MuiTab-root": {
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          borderRadius: "12px",
                          marginRight: "8px",
                        },
                        "& .Mui-selected": {
                          backgroundColor: "rgba(59, 130, 246, 0.1)",
                        },
                      }}
                    >
                      <Tab
                        label={
                          <div className="flex items-center gap-1 md:gap-2">
                            <LocalLibraryOutlinedIcon fontSize="small" />
                            <span className="hidden sm:inline">
                              Study Centre Seat Availability
                            </span>
                            <span className="sm:hidden">Library</span>
                          </div>
                        }
                      />
                    </Tabs>

                    {activeTab === 0 && (
                      <div className="mt-2 md:mt-4 bg-white rounded-lg p-2 md:p-4 shadow-sm overflow-hidden">
                        <SeatMap
                          data={librarySeats}
                          student={profile}
                          onSeatClick={handleLibrarySeatClick}
                          isAdminView={false}
                          refreshData={fetchAllData}
                          viewMode="seat"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Library History */}
          {selectedTab === "Library History" && <LibraryBookingHistory />}

          {/* 🔹 Renew Booking – vertical card like Review page */}
          {selectedTab === "Renew Booking" && (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-3 md:p-6 flex flex-col">
              <Box className="flex-1 flex items-center justify-center">
                <Card className="w-full max-w-4xl shadow-xl rounded-2xl border border-gray-200">
                  <CardContent className="p-4 md:p-6">
                    {/* Header (similar to Review) */}
                    <Box className="flex items-center gap-3 mb-5">
                      <Box className="p-3 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 shadow-md">
                        <AutorenewIcon className="text-white" />
                      </Box>
                      <Box>
                        <Typography
                          variant="h5"
                          className="font-bold text-gray-800 mb-1"
                        >
                          Renew Library Booking
                        </Typography>
                        <Typography
                          variant="body2"
                          className="text-gray-600"
                        >
                          Check your current booking status, choose tenure and
                          preview your bill before renewal.
                        </Typography>
                      </Box>
                    </Box>

                    {/* Body */}
                    {renewLoading ? (
                      <Box className="flex flex-col items-center justify-center py-10 space-y-2">
                        <CircularProgress size={32} />
                        <Typography
                          variant="body2"
                          className="text-gray-500"
                        >
                          Loading your booking details...
                        </Typography>
                      </Box>
                    ) : !renewalInfo || !renewalInfo.has_booking ? (
                      <Box className="bg-gray-50 rounded-xl p-4 border border-dashed border-gray-300">
                        <Typography
                          variant="body2"
                          className="text-gray-700"
                        >
                          You don&apos;t have any{" "}
                          <span className="font-semibold">approved</span>{" "}
                          library booking at the moment.
                        </Typography>
                      </Box>
                    ) : (
                      // 🔸 Vertical layout: first details, then bill section
                      <Box className="space-y-4">
                        {/* Current booking details */}
                        <Box className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                          <Typography
                            variant="subtitle2"
                            className="text-gray-700 mb-2"
                          >
                            Current Booking Details
                          </Typography>
                          <Box className="grid grid-cols-1 gap-2 text-sm">
                            <Typography className="text-gray-800">
                              <span className="font-semibold">Seat:</span>{" "}
                              {renewalInfo.booking?.seat_number || "N/A"}
                            </Typography>
                            <Typography className="text-gray-800">
                              <span className="font-semibold">Status:</span>{" "}
                              {renewalInfo.booking?.status}
                            </Typography>
                            <Typography className="text-gray-800">
                              <span className="font-semibold">End date:</span>{" "}
                              {renewalInfo.booking?.end_date}
                            </Typography>
                            <Typography className="text-gray-800">
                              <span className="font-semibold">
                                Days remaining:
                              </span>{" "}
                              {renewalInfo.booking?.days_remaining}
                            </Typography>
                          </Box>

                          {!renewalInfo.can_renew && (
                            <Box className="mt-4 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                              <Typography
                                variant="body2"
                                className="text-yellow-800 font-medium"
                              >
                                Renewal will be available when your booking is
                                within{" "}
                                <span className="font-bold">15 days</span> of
                                its end date.
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Tenure + Bill preview (Review-style card) */}
                        <Box className="bg-white rounded-xl p-4 border border-yellow-200 shadow-sm">
                          <Typography
                            variant="subtitle2"
                            className="text-gray-800 mb-2"
                          >
                            Renewal Options &amp; Bill Summary
                          </Typography>

                          {renewalInfo.can_renew ? (
                            <>
                              {/* Tenure dropdown */}
                              <FormControl
                                fullWidth
                                size="small"
                                className="mb-3"
                              >
                                <InputLabel id="renew-cycle-label">
                                  Renewal Cycle (months)
                                </InputLabel>
                                <Select
                                  labelId="renew-cycle-label"
                                  label="Renewal Cycle (months)"
                                  value={renewMonths}
                                  onChange={(e) =>
                                    setRenewMonths(Number(e.target.value))
                                  }
                                >
                                  <MenuItem value={3}>3 months</MenuItem>
                                  <MenuItem value={6}>6 months</MenuItem>
                                  <MenuItem value={9}>9 months</MenuItem>
                                  <MenuItem value={12}>
                                    12 months (1 year)
                                  </MenuItem>
                                </Select>
                              </FormControl>

                              {/* Bill table */}
                              <Box className="bg-gray-50 rounded-lg p-3 border border-gray-200 mb-3">
                                <Box className="flex items-center justify-between mb-2 text-sm">
                                  <Typography className="text-gray-700">
                                    Monthly fee
                                  </Typography>
                                  <Typography className="font-semibold text-gray-800">
                                    ₹{monthlyFee.toLocaleString("en-IN")}
                                  </Typography>
                                </Box>
                                <Box className="flex items-center justify-between mb-2 text-sm">
                                  <Typography className="text-gray-700">
                                    Tenure
                                  </Typography>
                                  <Typography className="font-semibold text-gray-800">
                                    {renewMonths} months
                                  </Typography>
                                </Box>
                                <Box className="flex items-center justify-between mb-2 text-sm">
                                  <Typography className="text-gray-700">
                                    Deposit
                                  </Typography>
                                  <Typography className="font-semibold text-gray-800">
                                    ₹{depositAmount.toLocaleString("en-IN")}{" "}
                                    <span className="text-xs text-gray-500">
                                      (not charged on renewal)
                                    </span>
                                  </Typography>
                                </Box>
                                <Box className="border-t border-gray-200 pt-2 mt-1 flex items-center justify-between">
                                  <Typography className="text-gray-800 font-semibold">
                                    Total payable
                                  </Typography>
                                  <Typography className="text-lg font-bold text-blue-600">
                                    ₹{totalAmount.toLocaleString("en-IN")}
                                  </Typography>
                                </Box>
                              </Box>

                              <Typography
                                variant="caption"
                                className="text-gray-500 block mb-3"
                              >
                                Monthly fee and other billing rules will follow
                                your existing invoice system. Renewal only
                                extends the booking end date. Deposit is{" "}
                                <span className="font-semibold">not</span>{" "}
                                charged again.
                              </Typography>

                              <Button
                                variant="contained"
                                size="medium"
                                onClick={handleRenewSubmit}
                                disabled={renewSubmitting}
                                startIcon={
                                  renewSubmitting ? (
                                    <CircularProgress
                                      size={16}
                                      color="inherit"
                                    />
                                  ) : (
                                    <AutorenewIcon />
                                  )
                                }
                              >
                                {renewSubmitting
                                  ? "Renewing..."
                                  : "Confirm Renewal"}
                              </Button>
                            </>
                          ) : (
                            <Typography
                              variant="body2"
                              className="text-gray-600"
                            >
                              When your booking enters the renewal window
                              (within 15 days of end date), you will be able to
                              select tenure and see estimated bill here.
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </div>
          )}

          {/* Complaints / Bill / Review */}
          {selectedTab === "Complaints" && <StudentComplaints />}
          {selectedTab === "Bill" && <StudentBill />}
          {selectedTab === "Review" && <StudentReview />}
        </Box>

        {/* 🔹 Designer Popup: Reminder only */}
        <Dialog
          open={renewModalOpen && !!renewalInfo?.booking}
          onClose={() => setRenewModalOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            <Box className="flex items-center gap-2">
              <Box className="p-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600">
                <AutorenewIcon className="text-white" />
              </Box>
              <Typography variant="subtitle1" className="font-semibold">
                Library Booking Ending Soon
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {renewalInfo?.booking ? (
              <Box className="space-y-3">
                <Typography variant="body2" className="text-gray-700">
                  Your study centre booking is nearing its end. Please renew
                  your seat in time to avoid interruption.
                </Typography>

                <Box className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                  <Typography variant="body2" className="text-gray-800">
                    <span className="font-semibold">Seat:</span>{" "}
                    {renewalInfo.booking.seat_number || "N/A"}
                  </Typography>
                  <Typography variant="body2" className="text-gray-800">
                    <span className="font-semibold">End date:</span>{" "}
                    {renewalInfo.booking.end_date}
                  </Typography>
                  <Typography variant="body2" className="text-gray-800">
                    <span className="font-semibold">Days remaining:</span>{" "}
                    {renewalInfo.booking.days_remaining}
                  </Typography>
                </Box>

                <Box className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                  <Typography variant="body2" className="text-blue-800">
                    Go to{" "}
                    <span className="font-semibold">Renew Booking</span> from
                    the left menu to extend your booking.
                  </Typography>
                </Box>

                <Typography variant="caption" className="text-gray-500 block">
                  This reminder will continue to show while your booking is in
                  the renewal window and until you renew or the booking ends.
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" className="text-gray-700">
                No renewal information available.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRenewModalOpen(false)}>OK</Button>
          </DialogActions>
        </Dialog>

        {/* Booking Form */}
        <LibraryBookingForm
          seatId={selectedSeatId}
          open={!!selectedSeatId}
          onClose={() => {
            setSelectedSeatId(null);
          }}
          onSuccess={() => {
            fetchAllData();
            fetchRenewalAlert(false);
          }}
        />
      </Box>
    </Box>
  );
};

export default StudentDashboard;
